import { Heart, Plane, Hotel, Package, Ship, Car, MapPin, ExternalLink, Star, TrendingDown, Ticket, Smartphone, CarTaxiFront, ShieldCheck, TrainFront } from "lucide-react";
import { useFavorites } from "@/context/FavoritesContext";
import { cardImage } from "@/lib/img";
import { Link } from "react-router-dom";
import SmartImage from "@/components/SmartImage";
import { cityImage } from "@/lib/cityImages";
import { citySlug } from "@/lib/travel";

const TYPE_IMG = {
  Hotel: "photo-1566073771259-6a8506099945", Flight: "photo-1436491865332-7a61a109cc05", Experiences: "photo-1469854523086-cc02fe5d8800",
  Car: "photo-1449965408869-eaa3f722e40d", eSIM: "photo-1512941937669-90a1b58e7e9c", Package: "photo-1507525428034-b723cf961d3e",
};
// Never blank: curated city photo when we have one, otherwise a photo for the deal type
const travelFallback = (item) => {
  const slug = citySlug(item.destination);
  const city = slug && cityImage(slug, 700);
  return city && !city.includes("photo-1476514525535") ? city
    : `https://images.unsplash.com/${TYPE_IMG[item.travel_type] || "photo-1488646953014-85cb44e25828"}?auto=format&fit=crop&w=700&q=80`;
};
import ShareButton from "@/components/ShareButton";
import { ArrowRight } from "lucide-react";
import { isSearchOnlyHotel, cityStayPath } from "@/lib/travel";

const TYPE_ICON = { Flight: Plane, Hotel: Hotel, Package: Package, Cruise: Ship, Car: Car, Experiences: Ticket, eSIM: Smartphone, Transfer: CarTaxiFront, Insurance: ShieldCheck, Train: TrainFront };

export default function TravelCard({ item, index = 0, wide = false }) {
  const { ids, toggle } = useFavorites();
  const fav = ids.has(item.id);
  const Icon = TYPE_ICON[item.travel_type] || Plane;
  return (
    <article data-testid="travel-card"
      className={`group bg-white rounded-xl border border-neutral-200 overflow-hidden flex flex-col ${wide ? "sm:flex-row" : ""} hover:shadow-lg hover:-translate-y-1 transition-[transform,box-shadow] duration-200 fade-up`}
      style={{ animationDelay: `${Math.min(index * 60, 400)}ms` }}>
      <div className={`relative aspect-[16/10] overflow-hidden bg-neutral-100 ${wide ? "sm:aspect-auto sm:w-[42%] sm:shrink-0 sm:h-auto sm:min-h-[230px] sm:[&_img]:absolute sm:[&_img]:inset-0" : ""}`}>
        <Link to={isSearchOnlyHotel(item) ? cityStayPath(item) : `/trip/${item.id}`} data-testid="travel-detail-link" aria-label={`See details: ${item.title}`} className="block w-full h-full">
          {item.image_url ? (
            <SmartImage src={cardImage(item.image_url, 700)} fallbackSrc={travelFallback(item)} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          ) : <div className="w-full h-full flex items-center justify-center text-neutral-300 font-display text-3xl">Travel</div>}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
        </Link>
        <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-white/95 backdrop-blur text-ink-950 text-xs font-bold px-2.5 py-1 rounded-md">
          <Icon className="w-3.5 h-3.5 text-brand" /> {item.travel_type === "Package" ? "Flight + Hotel" : item.travel_type}
        </span>
        {item.discount_percent > 0 && (
          <span className="absolute top-3 right-3 bg-brand text-white text-xs font-bold px-2.5 py-1 rounded-md">-{item.discount_percent}%</span>
        )}
        {item.benefit && item.discount_percent <= 0 && (
          <span data-testid="travel-benefit" className="absolute top-3 right-3 bg-emerald-600 text-white text-xs font-bold px-2.5 py-1 rounded-md shadow">{item.benefit}</span>
        )}
        {item.destination && (
          <span data-testid="travel-destination" className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 bg-ink-950/85 backdrop-blur text-white text-sm font-bold pl-2.5 pr-3 py-1.5 rounded-full shadow-lg ring-1 ring-white/10">
            <MapPin className="w-4 h-4 text-brand" /> {item.destination}
          </span>
        )}
        <div className="absolute bottom-3 right-14">
          <ShareButton path={`/trip/${item.id}`} title={`${item.title}${item.price ? ` — ${item.price}` : ""}`} image={item.image_url} testid="travel-share-btn" />
        </div>
        <button data-testid="travel-fav-btn" onClick={(e) => { e.preventDefault(); toggle(item); }}
          className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-white/90 hover:bg-white flex items-center justify-center transition-colors" aria-label="Save">
          <Heart className={`w-4 h-4 transition-colors ${fav ? "fill-brand text-brand" : "text-neutral-500"}`} />
        </button>
      </div>
      <div className={`${wide ? "p-5 sm:p-7" : "p-4"} flex flex-col flex-1 min-w-0`}>
        {item.travel_type === "Hotel" && item.stars > 0 && (
          <span className="flex items-center gap-0.5 mb-1" data-testid="travel-stars">
            {Array.from({ length: item.stars }).map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />)}
          </span>
        )}
        {item.provider && <span className="text-xs tracking-[0.15em] uppercase font-bold text-neutral-400 mb-1">{item.provider}</span>}
        <h3 className={`font-display font-bold text-ink-900 leading-snug line-clamp-2 ${wide ? "text-xl" : "text-[17px]"}`}>{item.title}</h3>
        {item.rating > 0 && (
          <span data-testid="travel-rating" className="flex items-center gap-1.5 mt-1.5 text-sm text-neutral-600">
            <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
            <b className="text-ink-950">{item.rating.toFixed(1)}</b>
            {item.review_count > 0 && <span>({item.review_count.toLocaleString()} reviews)</span>}
            {item.duration && <span className="text-neutral-400">· {item.duration}</span>}
          </span>
        )}
        {item.description && <p className={`text-sm text-neutral-600 leading-relaxed mt-2 ${wide ? "line-clamp-3" : "line-clamp-2"}`}>{item.description}</p>}
        {item.price_dropped && (
          <span data-testid="price-drop-badge" className="inline-flex items-center gap-1 self-start mt-3 -mb-1 text-xs font-bold text-green-700 bg-green-100 px-2 py-1 rounded-md">
            <TrendingDown className="w-3.5 h-3.5" /> Price dropped
          </span>
        )}
        <div className="flex items-baseline gap-2 mt-3 mb-4">
          {item.price && (item.provider === "Viator" || item.price_checked_at) && <span className="text-xs text-neutral-500">from</span>}
          {item.price && <span className="font-display font-extrabold text-2xl text-ink-950">{item.price}</span>}
          {item.price && item.price_unit && <span className="text-xs text-neutral-500" data-testid="travel-price-unit">/{item.price_unit}</span>}
          {item.original_price && <span className="text-sm text-neutral-400 line-through">{item.original_price}</span>}
          {item.price_total_usd > 0 && item.rental_days > 0 && <span className="text-xs font-semibold text-neutral-500" data-testid="travel-price-total">· US${item.price_total_usd.toLocaleString()} for {item.rental_days} days</span>}
        </div>
        {item.unavailable ? (
          <span data-testid="travel-unavailable" className="mt-auto inline-flex items-center justify-center w-full h-11 rounded-full bg-neutral-100 text-neutral-400 font-semibold text-sm">Deal ended</span>
        ) : isSearchOnlyHotel(item) ? (
          <Link data-testid="travel-cta" to={cityStayPath(item)}
            className={`mt-auto inline-flex items-center justify-center gap-1.5 w-full ${wide ? "sm:w-auto sm:self-start sm:px-8" : ""} h-11 rounded-full bg-brand hover:bg-brand-hover text-white font-semibold text-sm transition-colors`}>
            See {item.destination} hotels <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        ) : (
          <a data-testid="travel-cta" href={item.affiliate_url} target="_blank" rel="noopener noreferrer sponsored"
            data-aff-title={item.title || item.city} data-aff-kind="travel"
            className={`mt-auto inline-flex items-center justify-center gap-1.5 w-full ${wide ? "sm:w-auto sm:self-start sm:px-8" : ""} h-11 rounded-full bg-brand hover:bg-brand-hover text-white font-semibold text-sm transition-colors`}>
            View Deal <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
      </div>
    </article>
  );
}
